import type { CallScriptHistoryOf } from "./callScriptShared.types";

/** Employment-verification fields captured during a Call Script call. */
export interface CallScriptVerifiedFields {
  employerName: string;
  phoneNumberDialed: string;
  verificationOutsourced: boolean;
  originalNumberBad: boolean;
  verifiedWith: string;
  hireDate: string;
  employmentType: string;
  occupation: string;
  streetNumber: string;
  streetName: string;
  apartmentNumber: string;
  city: string;
  state: string;
  zip: string;
  employerPhone: string;
  differentWorkLocation: boolean;
  /** Physical Work Location fields (only shown/collected when differentWorkLocation is checked). */
  workLocationName: string;
  workLocationStreetNumber: string;
  workLocationStreetName: string;
  workLocationApartmentNumber: string;
  workLocationZip: string;
  workLocationCity: string;
  workLocationState: string;
  workLocationPhone: string;
  dateOfTermination: string;
  payFrequency: string;
  grossPayPerPeriod: number | "";
  avgHoursPerWeek: number | "";
}

export const EMPTY_CALL_SCRIPT_VERIFIED_FIELDS: CallScriptVerifiedFields = {
  employerName: "",
  phoneNumberDialed: "",
  verificationOutsourced: false,
  originalNumberBad: false,
  verifiedWith: "",
  hireDate: "",
  employmentType: "",
  occupation: "",
  streetNumber: "",
  streetName: "",
  apartmentNumber: "",
  city: "",
  state: "",
  zip: "",
  employerPhone: "",
  differentWorkLocation: false,
  workLocationName: "",
  workLocationStreetNumber: "",
  workLocationStreetName: "",
  workLocationApartmentNumber: "",
  workLocationZip: "",
  workLocationCity: "",
  workLocationState: "",
  workLocationPhone: "",
  dateOfTermination: "",
  payFrequency: "",
  grossPayPerPeriod: "",
  avgHoursPerWeek: "",
};

/**
 * One per-question verified-vs-saved comparison, as returned by recap-service
 * on a history entry (`answers[]`). `questionText` is resolved server-side, so
 * no separate catalog lookup is required to render it.
 */
export interface CallScriptHistoryAnswer {
  verificationAnswerPk: number;
  verificationQuestionPk: number;
  questionText: string;
  answerEntered: string;
  answerStored: string;
  isAnswerMatch: "Y" | "N";
}

/**
 * A saved Call Script call, as returned verbatim by recap-service under
 * `GET .../call-script/history`. Analyst identity and per-question answer
 * text are resolved server-side, so no additional catalog/user lookups are
 * needed to render a row.
 */
export interface CallScriptEntry {
  verificationPhoneCallPk: number;
  verificationOutcome: string;
  phoneNumberDialed: string;
  isVerificationOutsourced: "Y" | "N";
  isPhoneNoBad: "Y" | "N";
  hireDate: string;
  /** ISO datetime the call occurred. */
  occurredAt: string;
  analystCauPk: number;
  analystName: string;
  applicantEmploymentPk?: number;
  applicantAddressPk?: number;
  answers: CallScriptHistoryAnswer[];
  /**
   * Reconstructed from the top-level fields above plus `answers[]` (matched
   * against the question catalog by question text) — the BFF has
   * no flat `verifiedFields` object of its own. Fields with no question
   * counterpart (e.g. Physical Work Location address fields) are never
   * persisted and always come back blank.
   */
  verifiedFields: CallScriptVerifiedFields;
}

export type CallScriptHistory = CallScriptHistoryOf<CallScriptEntry>;
